import 'reflect-metadata'
import i18next from 'i18next'
import {inject, injectable} from 'inversify'
import {action, makeObservable, observable, computed} from 'mobx'
import {openErrorNotification, openSuccessNotification} from '@/utils'
import {fetchUserAsDefault} from '@/stores/auth/services'
import {LoginRequest} from '@/stores/auth/models'
import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'
import {DefaultMerchantStoreInterface} from '@/pages/Profile/components'
import {ProfileStoreInterface, ProfileStoreSymbol} from '@/pages/Profile'

@injectable()
export class DefaultMerchantStore implements DefaultMerchantStoreInterface {
    _profileStore: ProfileStoreInterface
    isLoading: boolean = false

    constructor(@inject(ProfileStoreSymbol) profileStore: ProfileStoreInterface) {
        this._profileStore = profileStore

        makeObservable(this, {
            isLoading: observable,

            savedDefaultMerchant: computed,
            options: computed,

            setDefaultMerchant: action.bound
        })
    }

    get savedDefaultMerchant() {
        return this._profileStore?.profileData?.merchants?.find((m) => m.isDefault)?.merchantId
    }

    get options() {
        return (
            this._profileStore?.profileData?.merchants?.map((merchant) => ({
                label: merchant.name,
                value: merchant.merchantId,
                subLabel: merchant.companyName
            })) || []
        )
    }

    setDefaultMerchant = async (data: Pick<LoginRequest, 'merchantId' | 'isDefault'>) => {
        this.isLoading = true
        try {
            const {error} = await fetchUserAsDefault(data)

            if (error) {
                openErrorNotification(error.message, 5)
            } else {
                openSuccessNotification(i18next.t('DefaultMerchant.successMessage'), 5)

                await this._profileStore.loadProfileData()
            }
        } catch (error) {
            captureSentryError({
                name: 'DefaultMerchantStore setDefaultMerchant Error',
                description: getErrorMessage(error, 'Failed to set default merchant'),
                fingerprint: ['default-merchant-store-set-default'],
                error
            })
            openErrorNotification(error.message, 5)
        } finally {
            this.isLoading = false
        }
    }
}
